/*
 * Deterministic sample data.
 *
 * Both clients can now emit telemetry (browser/telemetry.py on desktop,
 * src/services/telemetry.js on mobile), but collection is opt-in and there is
 * no ingest service standing yet — so until one exists this generator stands
 * in. It is seeded, so the same range always produces the same numbers (no
 * flicker on re-render, and screenshots stay comparable), and it is shaped
 * after the two real apps: the feature list mirrors featureslist.md and the
 * mobile src/screens tree, the versions mirror the shipping ones (desktop
 * 10.0.0, mobile 6.5.0), and the themes mirror browser/styles.py THEMES.
 *
 * Swap it out by pointing VITE_API_BASE_URL at a real service; see client.ts.
 */

import type {
  ByPlatform,
  DayPoint,
  ErrorRow,
  FeatureRow,
  GeoRow,
  Metric,
  OsVersionRow,
  PerfMetric,
  Platform,
  RangeKey,
  RetentionCohort,
  Segment,
  Snapshot,
  VersionRow,
} from './types';
import { PLATFORMS, PLATFORM_CLIENT, RANGES, zeroByPlatform } from './types';

/* ── Seeded PRNG ────────────────────────────────────────────────────── */

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ── Helpers ────────────────────────────────────────────────────────── */

/** Anchored to a fixed date so the generated history never shifts under you. */
const TODAY = new Date('2026-07-31T00:00:00Z');

function isoDay(offsetFromToday: number): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() + offsetFromToday);
  return d.toISOString().slice(0, 10);
}

/** Weekend dip — desktop browsing sags Sat/Sun, mobile rises. */
function weekendFactor(date: string, platform: Platform): number {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  if (dow !== 0 && dow !== 6) return 1;
  return PLATFORM_CLIENT[platform] === 'desktop' ? 0.82 : 1.07;
}

function round(n: number): number {
  return Math.round(n);
}

/**
 * Per-platform shape of the fleet. Windows dominates desktop, Android
 * dominates mobile, Linux is a small but engaged tail — the mix a
 * privacy-focused browser would realistically see.
 */
const PLATFORM_BASE: ByPlatform = {
  windows: 11_600,
  macos: 4_900,
  linux: 1_900,
  android: 19_400,
  ios: 8_200,
};

/** Fractional growth across the window, per platform. */
const PLATFORM_GROWTH: ByPlatform = {
  windows: 0.11,
  macos: 0.17,
  linux: 0.08,
  android: 0.24,
  ios: 0.21,
};

/**
 * A growth curve with seeded noise. `growth` is the fractional change across
 * the whole window, so a 90-day window and a 7-day window of the same series
 * stay consistent in shape.
 */
function series(
  seedKey: string,
  days: number,
  base: number,
  growth: number,
  noise: number,
  platform: Platform | null,
  /**
   * Counts swing with the weekend; rates do not. Applying the weekend factor
   * to a ratio would say the crash-free rate falls to 81% every Saturday,
   * which is meaningless — pass false for any rate-like series.
   */
  applyWeekend = true,
): { date: string; value: number }[] {
  const rnd = mulberry32(hashSeed(seedKey));
  const out: { date: string; value: number }[] = [];
  for (let i = 0; i < days; i++) {
    const date = isoDay(-(days - 1 - i));
    const t = days === 1 ? 1 : i / (days - 1);
    // Slight ease so growth looks organic rather than linear.
    const trend = base * (1 + growth * (t * 0.75 + t * t * 0.25));
    const wobble = 1 + (rnd() - 0.5) * 2 * noise;
    const weekend = applyWeekend && platform ? weekendFactor(date, platform) : 1;
    out.push({ date, value: Math.max(0, round(trend * wobble * weekend)) });
  }
  return out;
}

/** Builds a per-platform daily series from a scale applied to the base mix. */
function platformSeries(seedKey: string, days: number, scale: number, noise: number): DayPoint[] {
  const perPlatform = PLATFORMS.map((p) =>
    series(`${seedKey}:${p}`, days, PLATFORM_BASE[p] * scale, PLATFORM_GROWTH[p], noise, p),
  );
  return perPlatform[0].map((row, i) => {
    const point = { date: row.date, ...zeroByPlatform() } as DayPoint;
    PLATFORMS.forEach((p, pi) => {
      point[p] = perPlatform[pi][i].value;
    });
    return point;
  });
}

function totalOf(point: DayPoint): number {
  return PLATFORMS.reduce((sum, p) => sum + point[p], 0);
}

function metricFrom(points: number[], fractionOfWindowForDelta = 0.5): Metric {
  const value = points[points.length - 1] ?? 0;
  const split = Math.max(1, Math.floor(points.length * fractionOfWindowForDelta));
  const recent = points.slice(-split);
  const prior = points.slice(-split * 2, -split);
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
  const priorMean = mean(prior);
  const delta = priorMean === 0 ? 0 : (mean(recent) - priorMean) / priorMean;
  return { value, delta, history: points.slice(-24) };
}

/* ── Static reference data, mirrored from the two apps ──────────────── */

const DESKTOP_VERSIONS = ['10.0.0', '9.9.2', '9.9.0', '9.7.4', '9.5.1'];
const MOBILE_VERSIONS = ['6.5.0', '6.4.1', '6.4.0', '6.2.3', '6.0.0'];

/** browser/styles.py THEMES ∩ mobile src/themes/index.js. */
const THEME_NAMES = [
  'Pyraxis',
  'Midnight',
  'Snowfall',
  'Neon Pulse',
  'Ocean Breeze',
  'Rose Gold',
  'Emerald Forest',
  'Graphite',
  'Daybreak',
];

const SEARCH_ENGINES = ['DuckDuckGo', 'Google', 'Brave', 'Startpage', 'Bing', 'Ecosia'];

/** OS releases within each platform. */
const OS_VERSIONS: Record<Platform, string[]> = {
  windows: ['Windows 11', 'Windows 10'],
  macos: ['macOS 15', 'macOS 14', 'macOS 13'],
  linux: ['Ubuntu 24.04', 'Fedora 40', 'Arch', 'Debian 12'],
  android: ['Android 15', 'Android 14', 'Android 13', 'Android 12'],
  ios: ['iOS 18', 'iOS 17', 'iOS 16'],
};

/**
 * Feature list drawn from featureslist.md (desktop) and src/screens +
 * src/services (mobile). Platform availability is real: vertical tabs and the
 * task manager are desktop-only, the offline game is mobile-only.
 */
const FEATURE_DEFS: Omit<FeatureRow, 'adoption' | 'frequency' | 'trend'>[] = [
  { id: 'adblock', name: 'Ad & tracker blocking', category: 'Privacy' },
  { id: 'cookie-filter', name: 'Cookie banner filter', category: 'Privacy' },
  { id: 'fp-shield', name: 'Fingerprinting defenses', category: 'Privacy' },
  { id: 'site-blocker', name: 'Site blocker / focus timer', category: 'Privacy' },
  { id: 'containers', name: 'Container tabs', category: 'Privacy' },
  { id: 'doh', name: 'DNS-over-HTTPS', category: 'Privacy' },
  { id: 'permissions', name: 'Granular site permissions', category: 'Privacy' },
  { id: 'incognito', name: 'Private window', category: 'Privacy' },
  { id: 'tab-groups', name: 'Tab groups / islands', category: 'Tabs' },
  { id: 'vertical-tabs', name: 'Vertical tabs', category: 'Tabs' },
  { id: 'tab-snooze', name: 'Tab snoozing', category: 'Tabs' },
  { id: 'tab-preview', name: 'Tab preview on hover', category: 'Tabs' },
  { id: 'ai-chat', name: 'AI assistant', category: 'AI' },
  { id: 'ai-rewrite', name: 'Inline rewrite assistant', category: 'AI' },
  { id: 'ai-agent', name: 'Autonomous browsing agent', category: 'AI' },
  { id: 'semantic-search', name: 'Semantic history search', category: 'AI' },
  { id: 'translate', name: 'Private translation', category: 'AI' },
  { id: 'reader', name: 'Reader mode', category: 'Productivity' },
  { id: 'password-manager', name: 'Password manager', category: 'Productivity' },
  { id: 'hotkeys', name: 'Custom hotkeys', category: 'Productivity' },
  { id: 'web-panels', name: 'Sidebar web panels', category: 'Productivity' },
  { id: 'task-manager', name: 'Built-in task manager', category: 'Productivity' },
  { id: 'saver-mode', name: 'Memory / battery saver', category: 'Productivity' },
  { id: 'find-in-page', name: 'Find in page', category: 'Productivity' },
  { id: 'pip', name: 'Picture-in-picture', category: 'Media' },
  { id: 'rss', name: 'RSS feed reader', category: 'Media' },
  { id: 'pwa', name: 'Installed PWAs', category: 'Media' },
  { id: 'downloads', name: 'Download manager', category: 'Media' },
  { id: 'offline-game', name: 'Offline game', category: 'Media' },
  { id: 'sync', name: 'Encrypted sync', category: 'Sync' },
  { id: 'profiles', name: 'Workflow profiles', category: 'Sync' },
  { id: 'handoff', name: 'Cross-device handoff', category: 'Sync' },
  { id: 'extensions', name: 'Chrome Web Store extensions', category: 'Sync' },
];

const DESKTOP_ONLY = new Set([
  'vertical-tabs',
  'tab-preview',
  'web-panels',
  'task-manager',
  'hotkeys',
  'containers',
  'ai-agent',
  'extensions',
  'profiles',
]);
const MOBILE_ONLY = new Set(['offline-game']);

const GEO_DEFS: [string, string][] = [
  ['United States', 'US'],
  ['India', 'IN'],
  ['Germany', 'DE'],
  ['Brazil', 'BR'],
  ['United Kingdom', 'GB'],
  ['Indonesia', 'ID'],
  ['France', 'FR'],
  ['Canada', 'CA'],
  ['Japan', 'JP'],
  ['Nigeria', 'NG'],
  ['Poland', 'PL'],
  ['Australia', 'AU'],
];

const ERROR_DEFS: [string, Platform, ErrorRow['severity']][] = [
  ['RuntimeError: QWebEngineProfile destroyed while page alive', 'windows', 'critical'],
  ['TypeError: Cannot read property "uri" of undefined — TabSwitcher', 'android', 'critical'],
  ['OSError: [Errno 24] Too many open files — extensions_host', 'linux', 'serious'],
  ['NativeModule: WebView render process ran out of memory', 'android', 'serious'],
  ['KeyError: theme not found in SIDEBAR_COLORS', 'windows', 'warning'],
  ['AsyncStorage: quota exceeded while writing history', 'ios', 'serious'],
  ['TimeoutError: DoH resolver did not respond in 5000ms', 'macos', 'warning'],
  ['Unhandled promise rejection in cloudSync.push()', 'ios', 'warning'],
  ['QtWebEngine: GPU process crashed on Metal backend', 'macos', 'critical'],
  ['Segfault in skia rasterizer under Wayland', 'linux', 'serious'],
];

/** name, unit, budget, per-platform medians. */
const PERF_DEFS: [string, PerfMetric['unit'], number, ByPlatform][] = [
  ['Cold start', 'ms', 1500, { windows: 1380, macos: 1220, linux: 1180, android: 1010, ios: 860 }],
  ['Time to first paint', 'ms', 800, { windows: 660, macos: 590, linux: 610, android: 540, ios: 470 }],
  ['New tab open', 'ms', 150, { windows: 122, macos: 104, linux: 98, android: 101, ios: 84 }],
  ['Median page load', 'ms', 2000, { windows: 1210, macos: 1090, linux: 1140, android: 1560, ios: 1340 }],
  ['Memory per tab', 'MB', 150, { windows: 146, macos: 132, linux: 118, android: 92, ios: 74 }],
  ['Idle CPU wake-ups', 'ms', 60, { windows: 48, macos: 38, linux: 34, android: 29, ios: 21 }],
];

/* ── Generator ──────────────────────────────────────────────────────── */

/** Zipf-ish weights so the head dominates, like real distributions. */
function weightedSplit(seedKey: string, count: number): number[] {
  const rnd = mulberry32(hashSeed(seedKey));
  const weights = Array.from({ length: count }, (_, i) => 1 / Math.pow(i + 1, 0.85) + rnd() * 0.08);
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => w / sum);
}

/** Distributes each platform's total across `names`, so columns sum correctly. */
function buildSegments(seedKey: string, names: string[], totals: ByPlatform): Segment[] {
  const shares = Object.fromEntries(
    PLATFORMS.map((p) => [p, weightedSplit(`${seedKey}:${p}`, names.length)]),
  ) as Record<Platform, number[]>;

  return names.map((name, i) => {
    const row = { name, ...zeroByPlatform() } as Segment;
    PLATFORMS.forEach((p) => {
      row[p] = round(shares[p][i] * totals[p]);
    });
    return row;
  });
}

export function generateSnapshot(range: RangeKey): Snapshot {
  const days = RANGES.find((r) => r.key === range)!.days;
  // Always generate 2× the window so deltas have a real prior period to
  // compare against rather than a truncated one.
  const genDays = days * 2;
  const rnd = mulberry32(hashSeed(`snapshot:${range}`));

  const dauFull = platformSeries('dau', genDays, 1, 0.05);
  const dau = dauFull.slice(-days);
  const wau = platformSeries('wau', days, 2.24, 0.025);
  const mau = platformSeries('mau', days, 5.24, 0.012);
  const installsFull = platformSeries('installs', genDays, 0.062, 0.16);
  const churned = platformSeries('churned', days, 0.034, 0.14);

  const dauTotals = dauFull.map(totalOf);
  const mauTotals = mau.map(totalOf);
  const installTotals = installsFull.map(totalOf);

  const latest = dau[dau.length - 1];

  const stickinessHistory = dauTotals
    .slice(-days)
    .map((d, i) => d / (mauTotals[i] ?? mauTotals[mauTotals.length - 1]));

  // All three are rates or per-user averages, so none of them take the weekend
  // count factor — only their own noise.
  const sessionSecFull = series('sessionSec', genDays, 1290, 0.06, 0.035, null, false).map((p) => p.value);
  const sessionsPerUserFull = series('sessPerUser', genDays, 620, 0.04, 0.03, null, false).map(
    (p) => p.value / 100,
  );
  const crashFreeFull = series('crashFree', genDays, 99_320, 0.0035, 0.0009, null, false).map((p) =>
    Math.min(0.9999, p.value / 100_000),
  );
  const pageLoadsFull = dauTotals.map((d) => round(d * (17 + rnd() * 3)));

  const summary: Snapshot['summary'] = {
    dau: metricFrom(dauTotals),
    mau: metricFrom(mauTotals),
    newUsers: metricFrom(installTotals),
    stickiness: metricFrom(stickinessHistory),
    avgSessionSec: metricFrom(sessionSecFull),
    sessionsPerUser: metricFrom(sessionsPerUserFull),
    crashFreeRate: metricFrom(crashFreeFull),
    pageLoads: metricFrom(pageLoadsFull),
  };

  /*
   * Versions — the two clients get different adoption curves on purpose:
   * desktop ships as a packaged app the user must update, so its tail is long,
   * while mobile rides the store's auto-update and converges fast. Windows
   * lags hardest; Linux users update most aggressively.
   */
  const VERSION_SHARE: Record<Platform, number[]> = {
    windows: [0.4, 0.25, 0.16, 0.12, 0.07],
    macos: [0.49, 0.23, 0.14, 0.09, 0.05],
    linux: [0.63, 0.19, 0.1, 0.05, 0.03],
    android: [0.69, 0.17, 0.08, 0.04, 0.02],
    ios: [0.76, 0.14, 0.06, 0.03, 0.01],
  };

  const versions: VersionRow[] = PLATFORMS.flatMap((platform) => {
    const list = PLATFORM_CLIENT[platform] === 'desktop' ? DESKTOP_VERSIONS : MOBILE_VERSIONS;
    const gap = PLATFORM_CLIENT[platform] === 'desktop' ? 21 : 17;
    const r = mulberry32(hashSeed(`ver:${platform}`));
    return list.map((version, i) => ({
      version,
      platform,
      users: round(latest[platform] * VERSION_SHARE[platform][i]),
      share: VERSION_SHARE[platform][i],
      // Older builds crash more — the whole point of watching this table.
      crashFreeRate: Math.min(0.9998, 0.9975 - i * 0.0034 - r() * 0.0008),
      releasedOn: isoDay(-(i * gap + 6)),
      isLatest: i === 0,
    }));
  });

  /* Features — adoption is stable per feature, so seed off the id. */
  const features: FeatureRow[] = FEATURE_DEFS.map((def) => {
    const r = mulberry32(hashSeed(`feature:${def.id}`));
    // Privacy features are the reason people install this browser, so they
    // sit high; the exotic ones sit low. That ordering is intentional.
    const ceiling =
      def.category === 'Privacy' ? 0.94 : def.category === 'Tabs' ? 0.62 : def.category === 'AI' ? 0.48 : 0.7;
    const floor = def.category === 'Privacy' ? 0.34 : 0.04;
    const baseAdoption = floor + r() * (ceiling - floor);

    const adoption = {} as ByPlatform<number | null>;
    for (const p of PLATFORMS) {
      const client = PLATFORM_CLIENT[p];
      const absent =
        (DESKTOP_ONLY.has(def.id) && client === 'mobile') || (MOBILE_ONLY.has(def.id) && client === 'desktop');
      if (absent) {
        adoption[p] = null;
        continue;
      }
      // Linux users lean harder on privacy and power features; iOS leans less.
      const lean = p === 'linux' ? 1.12 : p === 'ios' ? 0.88 : 1;
      adoption[p] = Math.min(0.98, baseAdoption * (0.78 + r() * 0.36) * lean);
    }

    return { ...def, adoption, frequency: 0.8 + r() * 22, trend: (r() - 0.38) * 0.42 };
  }).sort((a, b) => {
    const mean = (f: FeatureRow) => {
      const vals = PLATFORMS.map((p) => f.adoption[p]).filter((v): v is number => v !== null);
      return vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : 0;
    };
    return mean(b) - mean(a);
  });

  /* Retention — 8 weekly cohorts, classic decay with a plateau. */
  const offsets = [0, 1, 3, 7, 14, 21, 30];
  const retention: RetentionCohort[] = Array.from({ length: 8 }, (_, ci) => {
    const weeksAgo = 7 - ci;
    const r = mulberry32(hashSeed(`cohort:${range}:${ci}`));
    // Anchor the curve on day 1 rather than on day 0. A power decay from 1.0
    // puts day 1 at 100%, which no real cohort ever does — everyone who
    // installs is present on day 0 by definition, and a chunk never returns.
    const day1 = 0.54 + ci * 0.008 + r() * 0.07;
    return {
      cohort: isoDay(-(weeksAgo * 7 + 30)),
      size: round(4200 + r() * 2600 + ci * 180),
      days: offsets.map((d, di) => {
        // Only report an offset once the cohort is old enough to have it.
        const maxAge = weeksAgo * 7 + 30;
        if (d > maxAge) return null;
        if (di === 0) return 1;
        return Math.min(0.95, Math.max(0.03, day1 * Math.pow(d, -0.28) * (0.97 + r() * 0.06)));
      }),
    };
  });

  const errors: ErrorRow[] = ERROR_DEFS.map(([title, platform, severity], i) => {
    const r = mulberry32(hashSeed(`err:${title}`));
    const events = round((severity === 'critical' ? 5400 : severity === 'serious' ? 2100 : 720) * (0.5 + r()));
    const list = PLATFORM_CLIENT[platform] === 'desktop' ? DESKTOP_VERSIONS : MOBILE_VERSIONS;
    return {
      id: `PYX-${1200 + i * 37}`,
      title,
      platform,
      events,
      users: round(events * (0.18 + r() * 0.22)),
      firstSeen: isoDay(-round(12 + r() * 70)),
      lastSeen: isoDay(-round(r() * 2)),
      version: list[round(r() * 2)],
      severity,
    };
  }).sort((a, b) => b.events - a.events);

  const perf: PerfMetric[] = PERF_DEFS.map(([name, unit, budget, byPlatform]) => ({
    name,
    unit,
    byPlatform,
    budget,
    lowerIsBetter: true,
  }));

  const geoTotal = totalOf(latest);
  const geoShares = weightedSplit('geo', GEO_DEFS.length);
  const geo: GeoRow[] = GEO_DEFS.map(([country, code], i) => ({
    country,
    code,
    users: round(geoShares[i] * geoTotal),
    share: geoShares[i],
  }));

  /*
   * OS releases — each platform's own list is normalized against that
   * platform's DAU, so every column adds up to the total it breaks down.
   */
  const osVersions: OsVersionRow[] = PLATFORMS.flatMap((platform) => {
    const names = OS_VERSIONS[platform];
    const shares = weightedSplit(`osv:${platform}`, names.length);
    return names.map((name, i) => {
      const row = { name, platform, ...zeroByPlatform() } as OsVersionRow;
      row[platform] = round(shares[i] * latest[platform]);
      return row;
    });
  });

  const sessionBuckets = ['<1m', '1–3m', '3–10m', '10–30m', '30m–1h', '1h+'];
  /** Desktop skews long, mobile skews short — weights per client. */
  const SESSION_WEIGHTS: Record<'desktop' | 'mobile', number[]> = {
    desktop: [0.09, 0.14, 0.23, 0.26, 0.17, 0.11],
    mobile: [0.16, 0.24, 0.28, 0.19, 0.09, 0.04],
  };

  const sessionLength = sessionBuckets.map((bucket, i) => {
    const row = { bucket, ...zeroByPlatform() };
    for (const p of PLATFORMS) {
      row[p] = round(latest[p] * SESSION_WEIGHTS[PLATFORM_CLIENT[p]][i]);
    }
    return row;
  });

  const hourly: Snapshot['engagement']['hourly'] = [];
  for (let weekday = 0; weekday < 7; weekday++) {
    for (let hour = 0; hour < 24; hour++) {
      const r = mulberry32(hashSeed(`hm:${weekday}:${hour}:${range}`));
      // Two humps: a work-hours peak and a bigger evening peak.
      const work = Math.exp(-Math.pow(hour - 11, 2) / 14);
      const evening = Math.exp(-Math.pow(hour - 21, 2) / 10) * 1.25;
      const night = hour < 6 ? 0.12 : 1;
      const weekendShift = weekday === 0 || weekday === 6 ? 0.88 : 1;
      hourly.push({
        hour,
        weekday,
        sessions: round((work + evening) * night * weekendShift * 2600 * (0.9 + r() * 0.2)),
      });
    }
  }

  const liveNow = zeroByPlatform();
  for (const p of PLATFORMS) {
    liveNow[p] = round(latest[p] * (PLATFORM_CLIENT[p] === 'desktop' ? 0.041 : 0.036));
  }

  return {
    generatedAt: new Date().toISOString(),
    range,
    isMock: true,
    liveNow,
    summary,
    activeUsers: { dau, wau, mau, installs: installsFull.slice(-days), churned },
    osVersions,
    versions,
    features,
    retention,
    errors,
    perf,
    geo,
    engagement: { sessionLength, hourly },
    themes: buildSegments('themes', THEME_NAMES, latest),
    searchEngines: buildSegments('engines', SEARCH_ENGINES, latest),
  };
}
